import { AsyncHelper } from 'src/app/helpers/async.helper';
import { PathHelper } from 'src/app/helpers/path.helper';
import { IFileSecuritySettings } from 'src/app/interfaces/io/file-security-settings.interface';
import { IFile } from 'src/app/interfaces/io/file.interface';
import { FileModel } from 'src/app/models/io/file-model';
import { IFileDriver } from 'src/app/services/io/drivers/file-driver.interface';

export class ExampleDriver implements IFileDriver {
  private driveContent: any = null;

  constructor() {
  }

  async mount(unit: string, settings: string) {
    const response = await fetch(settings, { cache: "no-cache" });
    this.driveContent = await response.json();
    // console.log(this.driveContent);
  }

  isReadOnly() {
    return true;
  }

  getDriveType(): string {
    return "JSON Read-Only Storage";
  }

  private isFolder(metadata) {
    return Array.isArray(metadata.content);
  }

  async readTextFile(path: string) {
    await AsyncHelper.waitFor(() => this.driveContent);

    const split = path.split("/");
    let current = { name: "root", content: this.driveContent };

    if (split[0] === "" || split[0].includes(":")) {
      split.splice(0, 1);
    }

    do {
      const currentPath = split.shift();
      current = current.content.find(m => m.name === currentPath);
    } while (split.length && current && this.isFolder(current));

    if (current && !this.isFolder(current)) {
      if (current["link-content"]) {
        const finalUrl = current["link-content"];
        const req: RequestInit = {
          method: "GET",
          cache: "no-cache",
          headers: {
            "Content-Type": "text/plain",
          },
        };

        const response = await fetch(finalUrl, req);

        if (response.ok) {
          const responseData = await response.text();
          return responseData;
        }
        else {
          throw new Error(response.statusText);
        }
      }

      if (typeof current.content === "object") {
        return JSON.stringify(current.content);
      }

      return "" + current.content;
    }

    throw new Error(`File not found: ${path}`);
  }

  async list(path: string): Promise<IFile[]> {
    await AsyncHelper.waitFor(() => this.driveContent);

    if (!path) {
      return [];
    }

    if (path.length === 2 && path[1] === ":") {
      return (this.driveContent as Array<{
        name: string,
        icon: string,
        content: any
      }>).map(m => new FileModel(Object.assign(m, {
        path: "/" + m.name,
        properties: {
          isDirectory: true
        }
      })));
    }

    if (path.indexOf(":") === 1) {
      path = path.substr(2);
    }

    const parts = path.split("/").slice(1);
    let currentDirFiles: Array<{
      name: string,
      icon: string,
      content: any,
      security?: IFileSecuritySettings
    }> = null;

    if (parts.length === 0) {
      throw new Error(`The directory ${path} does not exists`);
    }

    for (const part of parts) {
      let node: any;

      if (!currentDirFiles) {
        node = this.driveContent.find(m => m.name === part);
      }
      else {
        node = currentDirFiles.find(m => m.name === part);
      }

      if (!node) {
        throw new Error(`The directory ${path} does not exists`);
      }

      let files = node.content as Array<{
        name: string,
        icon: string,
        content: any,
        security?: IFileSecuritySettings,
        path?: string
      }>;

      files = JSON.parse(JSON.stringify(files));
      files.forEach(m => m.path = PathHelper.join(path, m.name));

      currentDirFiles = files;
    }

    return this.getFilesFromJsonContent(currentDirFiles);
  }

  async writeTextFile(path: string, content: string): Promise<void> {
    throw new Error("The unit is read-only.");
  }

  async exists(path: string): Promise<boolean> {
    await AsyncHelper.waitFor(() => this.driveContent);

    if (!path) {
      return false;
    }

    if (path.length === 2 && path[1] === ":") {
      return true;
    }

    if (path.indexOf(":") === 1) {
      path = path.substr(2);
    }

    const parts = path.split("/").slice(1);
    let currentDirFiles: Array<{
      name: string,
      icon: string,
      content: any,
      security?: IFileSecuritySettings
    }> = null;

    if (parts.length === 0) {
      return false;
    }

    for (const part of parts) {
      let node: any;

      if (!currentDirFiles) {
        node = this.driveContent.find(m => m.name === part);
      }
      else {
        node = currentDirFiles.find(m => m.name === part);
      }

      if (!node) {
        return false;
      }

      let files = node.content as Array<{
        name: string,
        icon: string,
        content: any,
        security?: IFileSecuritySettings,
        path?: string
      }>;

      files = JSON.parse(JSON.stringify(files));
      files.forEach(m => m.path = PathHelper.join(path, m.name));

      currentDirFiles = files;
    }

    /** @todo Esto no está andando bien, fue copy paste de list, pensar bien y hacer */

    return true;
  }

  async rmdir(path: string): Promise<void> {
    throw new Error("The unit is read-only.");
  }

  async unlink(path: string): Promise<void> {
    throw new Error("The unit is read-only.");
  }

  private getFilesFromJsonContent(files: Array<{
    name: string,
    icon: string,
    content: any,
    security?: IFileSecuritySettings,
    path?: string
  }>) {
    return files.map(meta => {
      const c = typeof meta.content === "string" ? JSON.stringify(meta.content) : meta.content;
      const f = new FileModel({
        path: `${meta.path || meta.name}`,
        size: byteSize(c),
        icon: meta.icon,
        security: meta.security || null,
        properties: {
          isDirectory: Array.isArray(meta.content)
        }
      });

      return f;
    });
  }
}

const byteSize = str => new Blob([str]).size;
